import React from 'react'

import ColorUtil from 'color'
import styled from 'styled-components'

import { Container } from 'src/components/partials/Container'

const BaseColorsV2 = {
  black: '#1B1B1E',
  white: '#fff',

  gray1: '#fcfcfc',
  gray2: '#f9f9f9',
  gray3: '#f0f0f0',
  gray4: '#e8e8e8',
  gray5: '#e0e0e0',
  gray6: '#d9d9d9',
  gray7: '#cecece',
  gray8: '#bbbbbb',
  gray9: '#8d8d8d',
  gray10: '#838383',
  gray11: '#646464',
  gray12: '#202020',

  grayDark1: '#111111',
  grayDark2: '#191919',
  grayDark3: '#222222',
  grayDark4: '#2a2a2a',
  grayDark5: '#313131',
  grayDark6: '#3a3a3a',
  grayDark7: '#484848',
  grayDark8: '#606060',
  grayDark9: '#6e6e6e',
  grayDark10: '#7b7b7b',
  grayDark11: '#b4b4b4',
  grayDark12: '#eeeeee',

  electric1: '#FDFDFF',
  electric2: '#F7F9FF',
  electric3: '#EEF1FF',
  electric4: '#E1E8FF',
  electric5: '#D4DDFF',
  electric6: '#C2CFFF',
  electric7: '#ADBBFF',
  electric8: '#8F9FFF',
  electric9: '#4C4ED9',
  electric10: '#3B2FC9',
  electric11: '#3015B6',
  electric12: '#23276B',

  electricDark1: '#0C0F23',
  electricDark2: '#111430',
  electricDark3: '#1A1763',
  electricDark4: '#230E8D',
  electricDark5: '#2B18A2',
  electricDark6: '#3020AE',
  electricDark7: '#3327B4',
  electricDark8: '#3B2FC9',
  electricDark9: '#3D33CD',
  electricDark10: '#493CF2',
  electricDark11: '#A5A5FF',
  electricDark12: '#D8DFFF'
}

const BaseColors = {
  black: '#000',
  electric100: '#ECEEFF',
  electric200: '#DCE0FF',
  electric300: '#C4C9FF',
  electric400: '#A1A8FF',
  electric500: '#808AFF',
  electric600: '#6673FF',
  electric700: '#4C48EF',
  electric800: '#3B2FC9',
  electric900: '#0C009D',
  jade100: '#DAFFEF',
  jade200: '#C1FEE3',
  jade300: '#ADF5D6',
  jade400: '#95E9C4',
  jade500: '#80DDB4',
  jade600: '#6FD0A6',
  jade700: '#4DCA94',
  jade800: '#3DB983',
  jade900: '#32A673',
  midnight100: '#C2C3E9',
  midnight200: '#9698D0',
  midnight300: '#6668A1',
  midnight400: '#54568F',
  midnight500: '#3D3F7A',
  midnight600: '#2E3168',
  midnight700: '#1D1F4A',
  midnight800: '#111338',
  midnight900: '#090B2B',
  nectar100: '#FFF9F5',
  nectar200: '#FFF5ED',
  nectar300: '#FFF0E3',
  nectar400: '#FFE4CC',
  nectar500: '#FFDCBD',
  nectar600: '#FFD9B7',
  nectar700: '#FBCCA2',
  nectar800: '#F9C493',
  nectar900: '#F6BA83',
  pigeon100: '#F6F8FC',
  pigeon200: '#E8EDF4',
  pigeon300: '#D7DEE8',
  pigeon400: '#CAD0DC',
  pigeon500: '#B4BCCC',
  pigeon600: '#8B93A4',
  pigeon700: '#616A7D',
  pigeon800: '#404857',
  pigeon900: '#303744',
  pinata100: '#FFE9E8',
  pinata200: '#FFDDDB',
  pinata300: '#FFD1CF',
  pinata400: '#FFBEBB',
  pinata500: '#FFB1A9',
  pinata600: '#FFA197',
  pinata700: '#FF8D77',
  pinata800: '#FD7F67',
  pinata900: '#EE6F57',
  sky100: '#D4EAFF',
  sky200: '#B9DCFF',
  sky300: '#94C9FF',
  sky400: '#73B2F0',
  sky500: '#54A0EA',
  sky600: '#2473D1',
  sky700: '#034EB9',
  sky800: '#003D95',
  sky900: '#002E70',
  sunflower100: '#FFFAED',
  sunflower200: '#FFF5D9',
  sunflower300: '#FFEFC2',
  sunflower400: '#FFE9AD',
  sunflower500: '#FFE396',
  sunflower600: '#FFDD81',
  sunflower700: '#FFD86E',
  sunflower800: '#FFD152',
  sunflower900: '#FFCA38',
  white: '#FFFFFF'
}

export const BrandColors = Object.assign(BaseColors, {
  danger: BaseColors.pinata800,
  info: BaseColors.electric800,
  primary: BaseColors.electric800,
  primaryAccent: BaseColors.electric100,
  primaryStrong: BaseColors.electric900,
  success: BaseColors.jade800,
  text: BaseColors.midnight900,
  warning: BaseColors.sunflower800
})

export const BrandColorsV2 = Object.assign(BaseColorsV2, {
  danger: BaseColors.pinata800,
  info: BaseColors.electric800,
  primary: BaseColors.electric800,
  primaryAccent: BaseColors.electric100,
  primaryStrong: BaseColors.electric900,
  success: BaseColors.jade800,
  text: BaseColors.midnight900,
  warning: BaseColors.sunflower800
})

export const ProductColors = {
  portal: BrandColors.sky600,
  workspaces: BrandColors.pinata900
}

// These things should probably move somewhere or be reorganized
const SectionDividerDiv = styled.div<{
  backgroundColor?: string
}>`
  background-color: ${props => props.backgroundColor || 'transparent'};
  height: 1px;
  hr {
    height: 1px;
    border: 0;
  }
`
export const SectionDivider = ({
  backgroundColor,
  borderColor
}: {
  backgroundColor?: string
  borderColor?: string
}) => {
  return (
    <SectionDividerDiv backgroundColor={backgroundColor}>
      <Container>
        <hr
          style={{
            backgroundColor: `${borderColor || BrandTokens.borderLight}`,
            height: '1px'
          }}
        />
      </Container>
    </SectionDividerDiv>
  )
}

export const BrandTokens = {
  background10: BrandColors.pigeon100,
  backgroundDark: BrandColorsV2.black,
  backgroundLight: BrandColors.white,
  border90: BrandColors.pigeon900,
  borderDark: BrandColors.black,
  borderLight: BrandColors.pigeon300,
  borderMedium: BrandColors.pigeon700,
  borderRadius: 0,
  boxShadow16: `16px 16px ${BrandColors.black}`,
  boxShadow8: `8px 8px ${BrandColors.black}`,
  boxShadow4: `4px 4px ${BrandColors.black}`,
  boxShadowLight16: `16px 16px ${BrandColors.white}`,
  boxShadowLight8: `8px 8px ${BrandColors.white}`,
  boxShadowLight4: `4px 4px ${BrandColors.white}`,
  boxShadowOutline: `8px 8px 0 -2px ${BrandColors.white}, 8px 8px 0 ${BrandColors.black}`,
  buttonThemeDark: {
    borderColor: BrandColors.black,
    color: BrandColors.white,
    fill: BrandColors.black
  },
  buttonThemeDarkOutline: {
    borderColor: BrandColors.black,
    color: BrandColors.black,
    fill: 'transparent'
  },
  buttonThemeLight: {
    borderColor: BrandColors.white,
    color: BrandColors.black,
    fill: BrandColors.white
  },
  buttonThemeLightOutline: {
    borderColor: BrandColors.black,
    color: BrandColors.black,
    fill: 'transparent'
  },
  iconThemeDark: {
    primaryColor: BrandColors.black,
    secondaryColor: BrandColors.white
  },
  iconThemeLight: {
    primaryColor: BrandColors.white,
    secondaryColor: BrandColors.black
  },
  linkPrimary: BrandColors.primary,
  linkPrimaryHover: BrandColors.primaryStrong,
  textDark: BrandColors.black,
  textDark50: BrandColors.pigeon700,
  textDark90: BrandColors.pigeon900,
  textLight: BrandColors.white,
  textLight50: BrandColors.pigeon400
}

export const FadeColor = (color: string, amount?: number) => {
  return ColorUtil(color)
    .fade(amount ? amount : 0.5)
    .rgb()
    .string()
}

export const DarkenColor = (color: string, amount?: number) => {
  return ColorUtil(color)
    .darken(amount ? amount : 0.25)
    .rgb()
    .string()
}

export const LightenColor = (color: string, amount?: number) => {
  return ColorUtil(color)
    .lighten(amount ? amount : 0.075)
    .rgb()
    .string()
}
